import {
  queryOptions,
  useQueries,
  useQuery,
  useSuspenseQueries,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { create, keyResolver } from "@yornaath/batshit";
import type { Address } from "shared";
import { Api } from "@/api.config";
import { QueryKeys } from "@/LinkGen";

const batcher = create({
  fetcher: async (IDs: number[]) => {
    return Api.Address(IDs);
  },
  resolver: keyResolver("ID"),
});

function addressQueryOptions(ID: number | null | undefined) {
  return queryOptions({
    queryKey: QueryKeys.Address(ID),
    queryFn: () => {
      if (!ID) {
        return null;
      }

      return batcher.fetch(ID);
    },
  });
}

export function useSuspenseAddress(ID: number): Address;
export function useSuspenseAddress(ID: null): null;
export function useSuspenseAddress(ID: undefined): null;
export function useSuspenseAddress(
  ID: number | null | undefined,
): Address | null;
export function useSuspenseAddress(ID: number | null | undefined) {
  const { data } = useSuspenseQuery(addressQueryOptions(ID));

  return data;
}

export function useAddressesSuspense(IDs: number[]) {
  return useSuspenseQueries({
    queries: IDs.map(addressQueryOptions),
    combine: (results) => results.map((x) => x.data as Address),
  });
}

export function useAddresses(IDs: number[]): {
  data: Address[];
  isLoading: boolean;
};
export function useAddresses(IDs: number[]) {
  const queries = useQueries({
    queries: IDs.map((ID) => addressQueryOptions(ID)),
  });

  const data = queries.map((x) => x.data);
  const isLoading = queries.some((x) => x.isLoading);

  return { data, isLoading };
}

export function useAddressSearch(query: string) {
  return useQuery({
    queryKey: QueryKeys.Address.Search(query),
    queryFn: () => Api.AddressSearch(query).then((res) => res.data),
    enabled: query.length >= 1,
    staleTime: 0, // enforce data is always fresh :)
  });
}

export function useAddressPlacesSearch(query: string, sessionToken: string) {
  return useQuery({
    queryKey: QueryKeys.Address.Places(query, sessionToken),
    queryFn: () =>
      Api.AddressPlacesSearch({ query, sessionToken }).then((res) => res.data),
    enabled: query.length >= 1,
    staleTime: 0,
  });
}
